import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Compass, CheckCircle, Lock, Sparkles, User } from 'lucide-react';

export default function GuidedModeInfoModal({ onClose, isGuest = false }) {
  const navigate = useNavigate();

  const handleSignup = () => {
    onClose();
    navigate('/register');
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <Card 
        className="max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-card)', border: '2px solid var(--primary-blue)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-full" style={{ background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)' }}>
              <Compass className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl" style={{ color: 'var(--text-primary)' }}>
              Guided Mode
            </CardTitle>
          </div>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            A structured, step-by-step journey through healing and self-discovery
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* What is Guided Mode */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Sparkles className="w-5 h-5" style={{ color: 'var(--primary-blue)' }} />
              What is Guided Mode?
            </h3>
            <p className="text-base leading-relaxed mb-3" style={{ color: 'var(--text-primary)' }}>
              Guided Mode provides a curated, linear pathway through the app's educational content and tools. Instead of wondering where to start, you'll be guided through a logical sequence designed to help you:
            </p>
            <ul className="space-y-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-green)' }} />
                <span>Understand the dynamics of narcissistic abuse and family systems</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-green)' }} />
                <span>Learn how trauma affects your brain and nervous system</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-green)' }} />
                <span>Build personalized tools (SUDS chart, CalmGuard plans)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-green)' }} />
                <span>Choose a specialized pathway: De-escalation, Boundaries, or Emotional Patterns</span>
              </li>
            </ul>
          </div>

          {/* Access Levels */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Free Account */}
            <Card style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--primary-blue)' }}>
                  <User className="w-5 h-5" />
                  Free Account
                </h4>
                <p className="text-sm mb-3" style={{ color: 'var(--text-primary)' }}>
                  Explore the foundational content and get a feel for the guided journey:
                </p>
                <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Complete trauma screening quiz</li>
                  <li>• Access educational modules</li>
                  <li>• Build SUDS chart & CalmGuard plan</li>
                  <li>• View all branch options</li>
                </ul>
              </CardContent>
            </Card>

            {/* Premium */}
            <Card style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#f59e0b' }}>
                  <Sparkles className="w-5 h-5" />
                  Premium
                </h4>
                <p className="text-sm mb-3" style={{ color: 'var(--text-primary)' }}>
                  Unlock the complete healing journey with unlimited access:
                </p>
                <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                  <li>• All 5 specialized pathways</li>
                  <li>• Advanced modules & tools</li>
                  <li>• Progress saved permanently</li>
                  <li>• Unlimited CalmGuard plans</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {isGuest ? (
              <>
                <Button
                  onClick={handleSignup}
                  className="flex-1"
                  style={{ 
                    background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                    color: 'white'
                  }}
                >
                  Create Free Account
                </Button>
                <Button
                  onClick={handleUpgrade}
                  variant="outline"
                  className="flex-1"
                >
                  Upgrade to Premium
                </Button>
              </>
            ) : (
              <Button
                onClick={handleUpgrade}
                className="w-full"
                style={{ 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white'
                }}
              >
                <Lock className="w-4 h-4 mr-2" />
                Upgrade to Premium for Full Access
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
